// HR › Settings › Overtime, holidays & night pay (Report 55 OD-33; Report 58 §G, M1, M2). The labour-standards part of
// the statutory rules registry, read only: what the law requires, how Apparify uses each rule, where it comes from, and
// what decides whether it applies to this business. The owner confirms the business size here; nobody edits a rule.
// The server owns every rule, rate, outcome and permission; this only shows them and sends the owner's statement.
import { useCallback, useEffect, useState } from "react";
import { Link, useInRouterContext } from "react-router-dom";
import { ExternalLink } from "lucide-react";

import { businessApi } from "../../../../services/api";
import {
  Button, ConfirmDialog, ErrorState, Field, Input, Notice, SkeletonRows, Status, Surface, Table, TableContainer,
  TBody, Td, Th, THead, Tr, Textarea, cn, focusRing, plainError,
} from "../../../../components/ui";
import PayRuleApplicabilityList from "./PayRuleApplicabilityList";
import BusinessSizeDialog from "./BusinessSizeDialog";

const link = cn("rounded-control font-medium text-accent hover:underline", focusRing);
const AUTOMATION = { CALCULATED: "Calculated", REVIEWED: "Used, with a person's check", DOCUMENTED_ONLY: "Reference only" };
const KIND = { RETAIL_OR_SERVICE: "Retail or service establishment", OTHER: "Not a retail or service establishment" };
const HOLIDAY_WORD = {
  REGULAR_HOLIDAY_UNWORKED: "Regular holiday, not worked", REGULAR_HOLIDAY_WORKED: "Regular holiday, worked",
  REGULAR_HOLIDAY_OVERTIME: "Regular holiday, overtime", REGULAR_HOLIDAY_REST_DAY_WORKED: "Regular holiday on a rest day, worked",
  REGULAR_HOLIDAY_REST_DAY_OVERTIME: "Regular holiday on a rest day, overtime", SPECIAL_NON_WORKING_WORKED: "Special non-working day, worked",
  SPECIAL_NON_WORKING_OVERTIME: "Special non-working day, overtime", SPECIAL_NON_WORKING_REST_DAY_WORKED: "Special day on a rest day, worked",
  SPECIAL_NON_WORKING_REST_DAY_OVERTIME: "Special day on a rest day, overtime",
};

const day = (date) => (date ? new Date(`${String(date).slice(0, 10)}T00:00:00`).toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" }) : "");
const percent = (bp) => `${Number(bp) / 100}%`;

/** "125% of the hourly rate", "+10% of the rate for the hour", "6 days", "10:00 p.m. – 6:00 a.m.". */
export function ruleValueText(rule) {
  if (rule.key === "NIGHT_DIFFERENTIAL" && rule.rateBp) return `+${percent(rule.rateBp)} of the hour's rate, 10:00 p.m. – 6:00 a.m.`;
  if (rule.rateBp) return `${percent(rule.rateBp)} of the rate`;
  if (rule.thresholdValue != null) {
    const unit = { DAYS: "day", HOURS: "hour", MINUTES: "minute" }[rule.thresholdUnit] || "";
    const plural = Number(rule.thresholdValue) === 1 ? unit : `${unit}s`;
    const bound = { LTE: "at most", GTE: "at least", LT: "under", GT: "over" }[rule.comparator] || "";
    return `${bound ? `${bound} ` : ""}${rule.thresholdValue} ${plural}`.trim();
  }
  return "—";
}

function AppLink({ to, children }) {
  const inRouter = useInRouterContext();
  return inRouter ? <Link to={to} className={link}>{children}</Link> : <a href={to} className={link}>{children}</a>;
}

function SectionHead({ title, purpose, action = null }) {
  return (
    <div className="flex flex-col gap-2 px-4 pt-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h2 className="text-section-title text-foreground">{title}</h2>
        {purpose ? <p className="mt-0.5 max-w-[75ch] text-body-sm text-secondary">{purpose}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

const PRACTICE_WORD = { PAYS_HOLIDAY_PAY: "Pays holiday pay anyway", LAW_ONLY: "Pays only what the law requires" };
const CONFIRMATION_WORD = { CONFIRMED: "Confirmed from Apparify's records", ADJUSTED: "Confirmed with changes", TYPED: "Typed in before workforce confirmation" };

/** How the confirmed figure was made: "6 in Apparify + 1 not in Apparify − 1 not counted". */
export function sizeMakeup(fact) {
  if (!fact || fact.confirmation === "TYPED" || fact.apparifyCount == null) return null;
  const parts = [`${fact.apparifyCount} in Apparify`];
  if (fact.outsideCount) parts.push(`+ ${fact.outsideCount} not in Apparify`);
  if (fact.excluded?.length) parts.push(`− ${fact.excluded.length} not counted`);
  return parts.join(" ");
}

export default function PayRulesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const [deciding, setDeciding] = useState(null); // { record, decision }
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await businessApi.getHRPayRules());
      setError("");
    } catch (err) {
      setError(plainError(err, "The pay rules couldn't be loaded."));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <SkeletonRows rows={5} columns={3} label="Loading the pay rules" />;
  if (error && !data) return <ErrorState title="Pay rules couldn't be loaded" description={error} onRetry={load} />;

  const establishment = data.establishment || {};
  const current = establishment.current;
  const version = (data.laborStandards || []).find((item) => item.status === "in_use") || data.laborStandards?.[0] || null;
  const waiting = (data.coverageExceptions || []).filter((row) => row.status === "PENDING");
  const approved = (data.coverageExceptions || []).filter((row) => row.status === "APPROVED");

  return (
    <div className="space-y-4">
      <Notice title="What you can change here">
        Government rates cannot be changed. The owner confirms two facts about the business — its kind and its size —
        that decide whether the small-business exceptions apply. Employee exceptions are recorded on each employee&apos;s
        Pay tab. This is not legal advice.
      </Notice>
      {notice ? <Notice tone="success" role="status">{notice}</Notice> : null}

      <Surface as="section" aria-label="What applies to this business">
        <SectionHead
          title="What applies to this business"
          purpose={`As of ${day(data.today)}, for the business as a whole. An employee can still be excluded from a benefit by their duties; their Pay tab shows their own answer.`}
        />
        <PayRuleApplicabilityList rules={data.applicability || []} className="px-4 pb-2" />
      </Surface>

      <Surface as="section" aria-label="Business size">
        <SectionHead
          title="Business size"
          purpose="The kind of business and how many people work for it. Apparify counts its own employees; the owner confirms the figure and adds anyone Apparify does not know about."
          action={establishment.canManage ? <Button onClick={() => setEditing(true)}>{current ? "Confirm again" : "Confirm business size"}</Button> : null}
        />
        <div className="px-4 pb-4 pt-3 text-body-sm">
          {current ? (
            <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-3">
              <div><dt className="text-meta text-secondary">Kind</dt><dd className="text-foreground">{KIND[current.establishmentKind]}</dd></div>
              <div>
                <dt className="text-meta text-secondary">Workers</dt>
                <dd className="tabular-nums text-foreground">{current.regularWorkerCount}</dd>
                {sizeMakeup(current) ? <dd className="text-meta text-tertiary">{sizeMakeup(current)}</dd> : null}
              </div>
              <div><dt className="text-meta text-secondary">Applies from</dt><dd className="text-foreground">{day(current.effectiveFrom)}</dd></div>
              {current.holidayPayPractice ? (
                <div className="sm:col-span-3"><dt className="text-meta text-secondary">Holiday pay</dt><dd className="text-foreground">{PRACTICE_WORD[current.holidayPayPractice]}{current.holidayPracticeBasis ? ` — ${current.holidayPracticeBasis}` : ""}</dd></div>
              ) : null}
              {current.excluded?.length ? (
                <div className="sm:col-span-3"><dt className="text-meta text-secondary">Not counted</dt><dd className="text-foreground">{current.excluded.map((item) => `${item.name} (${item.note})`).join("; ")}</dd></div>
              ) : null}
              {current.outsideCount ? (
                <div className="sm:col-span-3"><dt className="text-meta text-secondary">Not in Apparify</dt><dd className="text-foreground">{current.outsideCount}{current.outsideNote ? ` — ${current.outsideNote}` : ""}</dd></div>
              ) : null}
              <div className="sm:col-span-3"><dt className="text-meta text-secondary">Based on</dt><dd className="max-w-[75ch] text-foreground">{current.basis}</dd></div>
              <div className="sm:col-span-3 text-meta text-tertiary">{CONFIRMATION_WORD[current.confirmation] || ""} · recorded by {current.recordedByName || "the owner"}.</div>
            </dl>
          ) : (
            <Status tone="warning" icon>Not confirmed yet. Until the owner confirms it, night shift differential is held for a decision and holiday pay follows the standard holiday rules.</Status>
          )}
          {current?.since && (current.since.joined.length || current.since.left.length) ? (
            <Notice tone="warning" className="mt-3">
              Since you confirmed: {[
                current.since.joined.length ? `${current.since.joined.map((person) => person.name).join(", ")} joined` : null,
                current.since.left.length ? `${current.since.left.map((person) => person.name).join(", ")} left` : null,
              ].filter(Boolean).join("; ")}. Apparify&apos;s records now point to {current.estimatedToday?.estimate} worker{current.estimatedToday?.estimate === 1 ? "" : "s"}.
              Confirm the size again if the business has changed size. Payroll holds a pay rule only when the change crosses one of the size limits.
            </Notice>
          ) : null}
          {current?.confirmation === "TYPED" ? <p className="mt-3 text-meta text-secondary">This figure was typed in before Apparify counted its own employees. Confirm the size again to check it against the employee records.</p> : null}
          {!current && establishment.workforceToday ? <p className="mt-3 text-meta text-secondary">Apparify has {establishment.workforceToday.total} employee{establishment.workforceToday.total === 1 ? "" : "s"} with an employment record today.</p> : null}
          {establishment.limitations ? <p className="mt-3 max-w-[75ch] text-meta text-tertiary">{establishment.limitations}</p> : null}
          {establishment.canManage && current ? (
            <p className="mt-3"><Button size="sm" variant="quiet" onClick={() => setCancelling(current)}>Cancel this record (entered by mistake)</Button></p>
          ) : null}
          {!establishment.canManage ? <p className="mt-3 text-meta text-secondary">Only the business owner confirms the business size.</p> : null}
        </div>
      </Surface>

      {waiting.length || approved.length ? (
        <Surface as="section" aria-label="Employee exceptions">
          <SectionHead title="Employee exceptions" purpose="Employees an approved exception excludes from a benefit, by their actual duties. Recorded on each employee's Pay tab." />
          <ul className="divide-y divide-subtle px-4 pb-2 pt-2 text-body-sm">
            {[...waiting, ...approved].map((row) => (
              <li key={row.id} className="flex flex-col gap-2 py-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                <div className="min-w-0">
                  <p className="text-foreground"><AppLink to={`/business/hr/employees/${row.memberId}/pay`}>{row.memberName || "Employee"}</AppLink> · {row.benefitLabel}</p>
                  <p className="text-meta text-secondary">From {day(row.effectiveFrom)}. {row.duties}</p>
                </div>
                <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
                  {row.status === "PENDING" ? <Status tone="warning" icon>Waiting for the owner</Status> : <span className="text-meta text-secondary">Approved</span>}
                  {row.status === "PENDING" && establishment.canManage ? (
                    <span className="flex gap-2">
                      <Button size="sm" onClick={() => setDeciding({ record: row, decision: "REJECT" })}>Reject</Button>
                      <Button size="sm" variant="primary" onClick={() => setDeciding({ record: row, decision: "APPROVE" })}>Approve</Button>
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </Surface>
      ) : null}

      {version ? (
        <Surface as="section" aria-label="Working-time pay rules">
          <SectionHead
            title="Working-time pay rules"
            purpose={`${version.title}. ${version.issuingAuthority}.`}
            action={<a href={version.sourceUrl} target="_blank" rel="noreferrer" className={cn(link, "inline-flex items-center gap-1 text-body-sm")}>Source <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" /></a>}
          />
          <p className="px-4 pt-1 text-meta text-tertiary" title={version.versionKey}>In force from {day(version.effectiveFrom)}{version.effectiveUntil ? ` to ${day(version.effectiveUntil)}` : ""} · checked by Apparify on {day(version.verifiedAt)}</p>
          <TableContainer className="mt-3 border-t border-subtle">
            <Table>
              <THead>
                <Tr>
                  <Th scope="col">Rule</Th>
                  <Th scope="col">Rate or limit</Th>
                  <Th scope="col">In Apparify</Th>
                </Tr>
              </THead>
              <TBody>
                {version.rules.map((rule) => (
                  <Tr key={rule.key}>
                    <Td className="align-top">
                      <span className="block font-medium text-foreground">{rule.title}</span>
                      <span className="mt-0.5 block max-w-[60ch] whitespace-normal text-meta text-secondary">{rule.explanation}</span>
                      <span className="mt-0.5 block text-meta text-tertiary">{rule.sourceReference}</span>
                    </Td>
                    <Td className="align-top tabular-nums">{ruleValueText(rule)}</Td>
                    <Td className="align-top">
                      {rule.automation === "DOCUMENTED_ONLY" ? <span className="text-secondary">{AUTOMATION[rule.automation]}</span> : AUTOMATION[rule.automation]}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </TableContainer>
          {version.exceptions?.length ? (
            <div className="border-t border-subtle px-4 py-3">
              <h3 className="text-label text-foreground">Who a benefit does not cover</h3>
              <ul className="mt-1 space-y-1 text-meta text-secondary">
                {version.exceptions.map((item) => (
                  <li key={`${item.benefit}-${item.code}`}>
                    <span className="text-foreground">{item.title}</span> ({{ PREMIUM_PAY: "overtime and premium pay", NIGHT_DIFFERENTIAL: "night shift differential", HOLIDAY_PAY: "holiday pay" }[item.benefit]}) — {item.conditions}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Surface>
      ) : null}

      {data.holidayRules ? (
        <Surface as="section" aria-label="Holiday pay rates">
          <SectionHead
            title="Holiday pay rates"
            purpose={data.holidayRules.automationText}
            action={<a href={data.holidayRules.sourceUrl} target="_blank" rel="noreferrer" className={cn(link, "inline-flex items-center gap-1 text-body-sm")}>Source <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" /></a>}
          />
          <ul className="grid gap-x-8 px-4 pb-4 pt-3 text-body-sm sm:grid-cols-2">
            {data.holidayRules.conditions.map((item) => (
              <li key={item.key} className="flex justify-between gap-4 border-b border-subtle py-1.5">
                <span className="text-secondary">{HOLIDAY_WORD[item.key] || item.key}</span>
                <span className="tabular-nums text-foreground">{percent(item.totalRateBp)}</span>
              </li>
            ))}
          </ul>
        </Surface>
      ) : null}

      <p className="text-meta text-tertiary">
        Government contributions and withholding tax are in <AppLink to="/business/hr/settings/government">Contributions &amp; tax</AppLink>. Minimum wage is set by regional wage orders, which Apparify does not check.
      </p>

      {editing ? (
        <BusinessSizeDialog
          current={current}
          onClose={() => setEditing(false)}
          onSaved={(result) => { setEditing(false); setData(result); setNotice("The business size was confirmed. Payroll uses it from the date given; recalculate any payroll not yet with Finance."); }}
        />
      ) : null}
      {cancelling ? (
        <CancelEstablishment
          fact={cancelling}
          onClose={() => setCancelling(null)}
          onDone={(result) => { setCancelling(null); setData(result); setNotice("The record was cancelled."); }}
        />
      ) : null}
      {deciding ? (
        <DecideException
          record={deciding.record}
          decision={deciding.decision}
          onClose={() => setDeciding(null)}
          onDone={async () => { setDeciding(null); await load(); setNotice(deciding.decision === "APPROVE" ? "Exception approved." : "Exception rejected."); }}
        />
      ) : null}
    </div>
  );
}

function CancelEstablishment({ fact, onClose, onDone }) {
  const [reason, setReason] = useState("");
  return (
    <ConfirmDialog
      title="Cancel this business size record?"
      tone="danger"
      facts={[
        { label: "Kind", value: KIND[fact.establishmentKind] },
        { label: "Workers", value: String(fact.regularWorkerCount) },
        { label: "Applies from", value: day(fact.effectiveFrom) },
      ]}
      consequence="The earlier record, if any, applies again. Payroll already with Finance is not changed."
      confirmLabel="Cancel record"
      cancelLabel="Keep record"
      confirmDisabled={reason.trim().length < 3}
      failureTitle="Not cancelled"
      onConfirm={async () => onDone(await businessApi.cancelHREstablishmentFact(fact.id, { reason: reason.trim() }))}
      onClose={onClose}
    >
      <Field label="Why it is cancelled" required hint="At least 3 characters.">
        <Input value={reason} maxLength={500} onChange={(event) => setReason(event.target.value)} />
      </Field>
    </ConfirmDialog>
  );
}

function DecideException({ record, decision, onClose, onDone }) {
  const [note, setNote] = useState("");
  const reject = decision === "REJECT";
  return (
    <ConfirmDialog
      title={`${reject ? "Reject" : "Approve"} this exception for ${record.memberName || "the employee"}?`}
      facts={[
        { label: "Benefit", value: record.benefitLabel },
        { label: "From", value: day(record.effectiveFrom) },
        { label: "Duties", value: record.duties },
      ]}
      consequence={reject
        ? "The employee stays covered by this benefit."
        : "Payroll stops paying this benefit to the employee from that date. Payroll already with Finance is not changed."}
      confirmLabel={reject ? "Reject exception" : "Approve exception"}
      cancelLabel="Go back"
      confirmDisabled={reject && note.trim().length < 5}
      failureTitle="Not saved"
      onConfirm={async () => onDone(await businessApi.decideHRPayRuleCoverage(record.id, { decision, note: note.trim() || undefined }))}
      onClose={onClose}
    >
      <Field label={reject ? "Why it is rejected" : "Note (optional)"} required={reject}>
        <Textarea rows={2} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} />
      </Field>
    </ConfirmDialog>
  );
}
